import React, { useEffect, useRef, useState } from "react"
import dayjs from "dayjs"
import type {
  ContactChannel,
  ContactCounterpart,
  ContactDetail,
  ContactDirection,
  ContactOutcome,
  ContactPhoneLine,
  ContactResult,
  DunningCaseItem,
  DunningContactLogEntry,
} from "../types"
import {
  CONTACT_CHANNEL_LABELS,
  CONTACT_CHANNEL_OPTIONS,
  CONTACT_COUNTERPART_LABELS,
  CONTACT_COUNTERPART_OPTIONS,
  CONTACT_DETAIL_LABELS,
  CONTACT_DETAIL_OPTIONS,
  CONTACT_DIRECTION_LABELS,
  CONTACT_OUTCOME_LABELS,
  CONTACT_PHONE_LINE_LABELS,
  CONTACT_PHONE_LINE_OPTIONS,
  CONTACT_RESULT_LABELS,
  CONTACT_RESULT_OPTIONS,
  NON_COUNTING_CONTACT_DETAILS,
  isSystemContactChannel,
} from "./shared"
import {
  toContactLogPayload,
  useDunningContactLogs,
  type ContactLogInput,
} from "../hooks/useDunningContactLogs"

type Props = {
  caseItem: DunningCaseItem
  onUpdated: (next: DunningCaseItem) => void
  /**
   * 読み取り専用の表示（AIドラフト検証の架空ケースなど）。記録・編集・削除の操作を出さない
   */
  readOnly?: boolean
}

const primaryBtn =
  "rounded-xl bg-primary px-4 py-2 text-[1.3rem] font-medium text-white hover:opacity-90 disabled:opacity-50"
const ghostBtn =
  "rounded-xl border border-gray-300 px-4 py-2 text-[1.3rem] font-medium text-black hover:bg-gray-50 disabled:opacity-50"
// global.scss が input に appearance:none を当てるため、督促画面では明示的にcheckboxとして復元する。
const checkbox =
  "h-4 w-4 cursor-pointer appearance-auto [-webkit-appearance:checkbox] accent-primary"

function nowLocal() {
  return dayjs().format("YYYY-MM-DDTHH:mm")
}

// smooth_api の DunningContactLogContactedAtValidator::FUTURE_CONTACTED_AT_MESSAGE と一致させている
const FUTURE_CONTACTED_AT_MESSAGE = "連絡日時が未来になっています。今より前の日時を入れてください"

/** 入力欄は分単位なので、今の分までは今として扱う */
const isFutureContactedAt = (contactedAtLocal: string) =>
  dayjs(contactedAtLocal).isAfter(dayjs(), "minute")

const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({
  title,
  onClose,
  children,
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
    <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-[1.6rem] font-semibold text-black">{title}</h3>
        <button
          type="button"
          onClick={onClose}
          className="text-[1.6rem] leading-none text-black hover:text-black"
          aria-label="閉じる"
        >
          ×
        </button>
      </div>
      {children}
    </div>
  </div>
)

const SelectButtons = <T extends string>({
  options,
  selected,
  onSelect,
}: {
  options: { code: T; label: string }[]
  selected: T | ""
  onSelect: (code: T) => void
}) => (
  <div className="mt-2 flex flex-wrap gap-2">
    {options.map((opt) => {
      const isSelected = selected === opt.code
      return (
        <button
          key={opt.code}
          type="button"
          aria-pressed={isSelected}
          onClick={() => onSelect(opt.code)}
          className={`rounded-xl border px-4 py-2 text-[1.3rem] font-medium transition-colors ${
            isSelected
              ? "border-primary bg-primary/10 text-primary"
              : "border-gray-300 text-black hover:bg-gray-50"
          }`}
        >
          {opt.label}
        </button>
      )
    })}
  </div>
)

type FormState = {
  channel: ContactChannel
  direction: ContactDirection
  contactedAtLocal: string
  /** 旧「結果」。旧レコードの編集互換用（新規入力は contactResult） */
  outcome: ContactOutcome | ""
  phoneLine: ContactPhoneLine | ""
  counterpart: ContactCounterpart | ""
  contactResult: ContactResult | ""
  contactDetail: ContactDetail | ""
  note: string
  countsAsContact: boolean
  /**
   * 担当者がチェック欄を手で変えたか。変えていれば内容・方向を選び直しても上書きしない。
   * 連絡手段を切り替えたときだけ false に戻し、新しい手段の既定値を入れ直す。
   */
  countsAsContactTouched: boolean
}

const emptyForm = (): FormState => ({
  channel: "phone",
  direction: "outbound",
  contactedAtLocal: nowLocal(),
  outcome: "",
  phoneLine: "",
  counterpart: "",
  contactResult: "",
  contactDetail: "",
  note: "",
  countsAsContact: true,
  countsAsContactTouched: false,
})

export function counterpartFieldLabel(channel: ContactChannel) {
  if (channel === "phone") return "架電先"
  if (channel === "sms" || channel === "email") return "送信先"
  return "連絡先"
}

/** 回線（どの電話から発信・送信したか）を記録する連絡手段。SMS も携帯・固定から送るため対象に含める */
const hasPhoneLine = (channel: ContactChannel) => channel === "phone" || channel === "sms"

const formFromEntry = (entry: DunningContactLogEntry): FormState => ({
  channel: entry.channel,
  direction: entry.direction,
  contactedAtLocal: dayjs(entry.contactedAt).format("YYYY-MM-DDTHH:mm"),
  outcome: entry.outcome ?? "",
  phoneLine: entry.phoneLine ?? "",
  counterpart: entry.counterpart ?? "",
  contactResult: entry.contactResult ?? "",
  contactDetail: entry.contactDetail ?? "",
  note: entry.note ?? "",
  countsAsContact: entry.countsAsContact,
  // 保存済みの値は担当者が決めたものとして扱い、内容を直しただけでは変えない
  countsAsContactTouched: true,
})

/** 連絡手段・方向・内容から決まる「連絡回数に数えるか」の既定値 */
const defaultCountsAsContact = (
  form: Pick<FormState, "channel" | "direction" | "contactDetail">
): boolean =>
  !(
    form.channel === "phone" &&
    form.direction === "outbound" &&
    form.contactDetail !== "" &&
    NON_COUNTING_CONTACT_DETAILS.includes(form.contactDetail)
  )

/** チェック欄を担当者が変えていなければ、既定値に合わせる */
const applyDefaultCountsAsContact = (form: FormState): FormState =>
  form.countsAsContactTouched ? form : { ...form, countsAsContact: defaultCountsAsContact(form) }

const toInput = (form: FormState): ContactLogInput => ({
  channel: form.channel,
  direction: form.direction,
  contactedAtLocal: form.contactedAtLocal,
  outcome: form.outcome,
  // 回線は電話・SMS、内容は電話専用の項目。対象外の手段では送らない（切替時のクリア漏れ保険）
  phoneLine: hasPhoneLine(form.channel) ? form.phoneLine : "",
  contactDetail: form.channel === "phone" ? form.contactDetail : "",
  counterpart: form.counterpart,
  contactResult: form.contactResult,
  note: form.note,
  countsAsContact: form.countsAsContact,
})

const PHONE_LINE_CLASSES: Record<string, string> = {
  dunning_fixed: "bg-sky-100 text-sky-900",
  cs_fixed: "bg-violet-100 text-violet-900",
  mobile_1: "bg-amber-100 text-amber-900",
  mobile_2: "bg-emerald-100 text-emerald-900",
}

const ContactLogRow: React.FC<{
  entry: DunningContactLogEntry
  onEdit: () => void
  onDelete: () => void
  disabled: boolean
  readOnly: boolean
}> = ({ entry, onEdit, onDelete, disabled, readOnly }) => {
  const detailLabel = entry.contactDetail
    ? (CONTACT_DETAIL_LABELS[entry.contactDetail] ?? entry.contactDetail)
    : ""
  const body = [detailLabel, entry.note].filter(Boolean).join("。")
  // 送信した LINE の記録は接触回数の正本なので、メモの編集だけ許す（削除はサーバでも 422）
  const memoOnly = isLineContactChannel(entry.channel)
  const successfulContact =
    !memoOnly && (entry.contactResult === "success" || entry.outcome === "reached")

  return (
    <tr className={`border-b border-gray-300 align-top ${successfulContact ? "bg-pink-50" : ""}`}>
      <td className="break-words px-2 py-3 text-[1.2rem] text-black">
        {/* 日付と時刻の間に空白を入れる。span を並べるだけだと textContent が
          「2026/08/0710:01」になり、読み上げとコピーで日時が潰れる。 */}
        <time dateTime={entry.contactedAt ?? undefined}>
          <span className="block">
            {entry.contactedAt ? dayjs(entry.contactedAt).format("YYYY/MM/DD") : "—"}
          </span>{" "}
          <span>{entry.contactedAt ? dayjs(entry.contactedAt).format("HH:mm") : "—"}</span>
        </time>
      </td>
      <td className="break-words px-2 py-3 text-[1.2rem] font-medium text-black">
        {entry.recordedByName ?? "システム"}
      </td>
      <td className="px-2 py-3 text-[1.2rem] text-black">
        <span className="font-semibold">
          {CONTACT_CHANNEL_LABELS[entry.channel] ?? entry.channel}（
          {CONTACT_DIRECTION_LABELS[entry.direction] ?? entry.direction}）
        </span>
      </td>
      <td className="px-2 py-3 text-[1.15rem] font-semibold text-[#177a92]">
        {entry.phoneLine ? (
          <span
            className={`inline-block rounded-full px-2 py-1 ${PHONE_LINE_CLASSES[entry.phoneLine] ?? "bg-gray-100 text-gray-900"}`}
          >
            {CONTACT_PHONE_LINE_LABELS[entry.phoneLine] ?? entry.phoneLine}
          </span>
        ) : (
          "—"
        )}
      </td>
      <td className="px-2 py-3 text-[1.2rem] text-black">
        {entry.counterpart
          ? (CONTACT_COUNTERPART_LABELS[entry.counterpart] ?? entry.counterpart)
          : "—"}
      </td>
      <td className="px-2 py-3 text-[1.25rem] leading-relaxed text-black">
        {(entry.contactResult || entry.outcome) && (
          <span
            className={`mb-1 mr-2 inline-block rounded-full px-2 py-0.5 text-[1.05rem] font-medium ${
              successfulContact ? "bg-pink-100 text-pink-800" : "bg-gray-100 text-black"
            }`}
            title={
              isLineContactChannel(entry.channel)
                ? "LINE系の値は送信結果です（通話のような到達確認ではありません）"
                : undefined
            }
          >
            {entry.contactResult
              ? (CONTACT_RESULT_LABELS[entry.contactResult] ?? entry.contactResult)
              : (CONTACT_OUTCOME_LABELS[entry.outcome!] ?? entry.outcome)}
          </span>
        )}
        {body ? (
          <>
            <p className="whitespace-pre-wrap [overflow-wrap:anywhere]">{body}</p>
          </>
        ) : (
          "—"
        )}
        {!entry.countsAsContact && (
          <span className="mt-1 block text-[1.05rem] text-black">（カウント対象外）</span>
        )}
        {entry.deliveryUnconfirmed && (
          <span
            className="mt-1 block text-[1.05rem] text-black"
            title="配信結果が確定していない送信です。届いた可能性があるため接触回数に数えています"
          >
            （送達不明）
          </span>
        )}
      </td>
      {!readOnly && (
        <td className="break-words px-2 py-3">
          <div className="flex flex-col items-start gap-2">
            <button
              type="button"
              className="text-[1.2rem] text-black hover:text-primary disabled:opacity-50"
              onClick={onEdit}
              disabled={disabled}
            >
              {memoOnly ? "メモ編集" : "編集"}
            </button>
            {!memoOnly && (
              <button
                type="button"
                className="text-[1.2rem] text-black hover:text-red-600 disabled:opacity-50"
                onClick={onDelete}
                disabled={disabled}
              >
                削除
              </button>
            )}
          </div>
        </td>
      )}
    </tr>
  )
}

/** 折りたたみ時に表示する件数 */
const COLLAPSED_COUNT = 6
const LINE_CONTACT_CHANNELS: ReadonlySet<ContactChannel> = new Set([
  "line_manual",
  "line_auto",
  "line_bulk",
  "liff",
])

const isLineContactChannel = (channel: ContactChannel) => LINE_CONTACT_CHANNELS.has(channel)

const uniqueEntries = (entries: DunningContactLogEntry[]) =>
  Array.from(new Map(entries.map((entry) => [entry.id, entry])).values())

export const ContactLogPanel: React.FC<Props> = ({ caseItem, onUpdated, readOnly = false }) => {
  const contact = caseItem.facts?.contact ?? null
  const { logContact, updateContactLog, deleteContactLog, loadContactLogs, isSubmitting, error } =
    useDunningContactLogs(caseItem.id)

  const [mode, setMode] = useState<"create" | "edit" | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm())
  const [contactedAtError, setContactedAtError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)
  const [showLineChannels, setShowLineChannels] = useState(false)
  const [additionalLogs, setAdditionalLogs] = useState<DunningContactLogEntry[]>([])
  const [historyNextPage, setHistoryNextPage] = useState(contact?.historyNextPage ?? null)
  // 表示用集計（compiled context）を持たないケース（履歴補完で作った closed ケースなど）は
  // facts.contact が無いため、履歴 API の1ページ目から読み込んで表示する。
  // 架空ケース（id 0 の検証用表示）では実在ケースの API を叩かない
  const loadsHistoryFromApi = contact == null && caseItem.id > 0
  const [historyApi, setHistoryApi] = useState<{
    status: "loading" | "loaded" | "failed"
    totalCount: number | null
  }>({ status: "loading", totalCount: null })
  const [historyReloadKey, setHistoryReloadKey] = useState(0)
  // 記録作成の冪等キー。フォームを開くたびに作り直し、同じ入力の再送では同じキーを使う
  // （内容を変えたら新しいキー）。保存できたら破棄する
  const pendingCreate = useRef<{ input: string; operationKey: string } | null>(null)

  // 履歴の内容が実際に変わったときだけ追加ページを破棄して取り直させる
  // （SWR は同内容なら参照を維持するため、再フェッチのたびに消えることはない。
  //   古い1ページ目と新しい追加ページの混在ビューを残すより安全側に倒す）
  useEffect(() => {
    setAdditionalLogs([])
    setHistoryNextPage(contact?.historyNextPage ?? null)
  }, [caseItem.id, contact?.historyNextPage, contact?.recent])

  useEffect(() => {
    if (!loadsHistoryFromApi) return

    let cancelled = false
    setHistoryApi({ status: "loading", totalCount: null })
    const loadFirstPage = async () => {
      const page = await loadContactLogs(1)
      if (cancelled) return
      if (!page) {
        setHistoryApi({ status: "failed", totalCount: null })
        return
      }
      setAdditionalLogs(uniqueEntries(page.data))
      setHistoryNextPage(page.meta.nextPage)
      setHistoryApi({ status: "loaded", totalCount: page.meta.totalCount })
    }
    loadFirstPage()
    return () => {
      cancelled = true
    }
  }, [caseItem.id, loadsHistoryFromApi, historyReloadKey])

  // 集計を持たないケースでは、記録・編集・削除の応答にも履歴が載らないので取り直す
  const reloadHistoryFromApi = () => {
    if (loadsHistoryFromApi) setHistoryReloadKey((key) => key + 1)
  }

  const reset = () => {
    setMode(null)
    setEditingId(null)
    setForm(emptyForm())
    setContactedAtError(null)
  }

  const apply = (next: DunningCaseItem | undefined) => {
    if (next) {
      onUpdated(next)
      reset()
      reloadHistoryFromApi()
    }
  }

  const openCreate = () => {
    pendingCreate.current = null
    setForm(emptyForm())
    setContactedAtError(null)
    setEditingId(null)
    setMode("create")
  }

  const openEdit = (entry: DunningContactLogEntry) => {
    setForm(formFromEntry(entry))
    setContactedAtError(null)
    setEditingId(entry.id)
    setMode("edit")
  }

  const submit = () => {
    // LINE の記録はメモだけ編集し日時は変えないので、手入力の記録だけ確かめる
    if (!isLineContactChannel(form.channel) && isFutureContactedAt(form.contactedAtLocal)) {
      setContactedAtError(FUTURE_CONTACTED_AT_MESSAGE)
      return
    }
    setContactedAtError(null)

    if (mode === "edit" && editingId != null) {
      updateContactLog(editingId, toInput(form)).then(apply)
    } else {
      const input = toInput(form)
      // 送る内容（前後の空白を除いたメモ等）とケース ID が同じときだけ前回のキーを使い回す
      const serialized = JSON.stringify({
        dunningCaseId: caseItem.id,
        payload: toContactLogPayload(input),
      })
      const operation =
        pendingCreate.current?.input === serialized
          ? pendingCreate.current
          : { input: serialized, operationKey: crypto.randomUUID() }
      pendingCreate.current = operation
      logContact(input, operation.operationKey).then((next) => {
        if (next && pendingCreate.current === operation) pendingCreate.current = null
        apply(next)
      })
    }
  }

  const handleDelete = (entry: DunningContactLogEntry) => {
    if (window.confirm("この連絡記録を削除しますか？")) {
      deleteContactLog(entry.id).then((next) => {
        if (!next) return
        onUpdated(next)
        reloadHistoryFromApi()
      })
    }
  }

  const handleLoadMore = async () => {
    if (historyNextPage == null) return

    setExpanded(true)
    const page = await loadContactLogs(historyNextPage)
    if (!page) return

    setAdditionalLogs((current) => uniqueEntries([...current, ...page.data]))
    setHistoryNextPage(page.meta.nextPage)
  }

  const legacyRecent = contact?.recent ?? []
  const additionalNonLine = additionalLogs.filter((entry) => !isLineContactChannel(entry.channel))
  const additionalLine = additionalLogs.filter((entry) => isLineContactChannel(entry.channel))
  const recentNonLine = uniqueEntries([
    ...(contact?.recentNonLine ??
      legacyRecent.filter((entry) => !isLineContactChannel(entry.channel))),
    ...additionalNonLine,
  ])
  const recentLine = uniqueEntries([
    ...(contact?.recentLine ?? legacyRecent.filter((entry) => isLineContactChannel(entry.channel))),
    ...additionalLine,
  ])
  const recent = [...(showLineChannels ? [...recentNonLine, ...recentLine] : recentNonLine)].sort(
    (left, right) => {
      const contactedAtDifference =
        dayjs(left.contactedAt).valueOf() - dayjs(right.contactedAt).valueOf()
      return contactedAtDifference !== 0 ? contactedAtDifference : left.id - right.id
    }
  )
  const lineMemoEdit = mode === "edit" && isLineContactChannel(form.channel)
  const lineEntryCount = contact?.lineCount ?? recentLine.length
  // 集計が無いときの LINE 件数は取得済みページ分だけなので、未取得ページがあれば下限として示す
  const lineCountIsLowerBound = loadsHistoryFromApi && historyNextPage != null
  const lineCountLabel =
    lineEntryCount > 10
      ? "10件以上"
      : lineCountIsLowerBound
        ? `${lineEntryCount}件以上`
        : `${lineEntryCount}件`
  // 取得窓は直近N件のまま、画面では古→新に並べる。折りたたみ時も
  // 古い側ではなく、オペレーターが確認すべき最新6件を残す。
  const visible = expanded ? recent : recent.slice(-COLLAPSED_COUNT)
  const hiddenCount = recent.length - visible.length
  const remainingHistoryCount = Math.max(
    (contact?.historyTotalCount ??
      (loadsHistoryFromApi ? historyApi.totalCount : null) ??
      recent.length) - recent.length,
    0
  )

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-[1.3rem] font-bold text-black">
          <span className="h-[14px] w-[4px] rounded-sm bg-primary" aria-hidden="true" />
          交渉記録
        </h2>
        {!readOnly && (
          <button type="button" className={primaryBtn} disabled={isSubmitting} onClick={openCreate}>
            連絡を記録
          </button>
        )}
      </div>

      {error && !mode && <p className="mt-3 text-[1.2rem] text-red-600">{error}</p>}

      <div className="mt-4">
        {recent.length === 0 && lineEntryCount === 0 ? (
          <p className="py-2 text-[1.3rem] text-black">
            {loadsHistoryFromApi && historyApi.status === "loading"
              ? "交渉記録を読み込んでいます…"
              : loadsHistoryFromApi && historyApi.status === "failed"
                ? "交渉記録を読み込めませんでした。"
                : "記録された連絡はまだありません。"}
          </p>
        ) : (
          <>
            <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
              <label className="flex cursor-pointer items-center gap-2 text-[1.2rem] text-black">
                <input
                  type="checkbox"
                  className={checkbox}
                  checked={showLineChannels}
                  onChange={(event) => setShowLineChannels(event.target.checked)}
                />
                LINE送信履歴も表示
              </label>
              {!showLineChannels && lineEntryCount > 0 && (
                <span className="text-[1.1rem] text-black">
                  LINE送信履歴 {lineCountLabel}を非表示中
                </span>
              )}
              {showLineChannels && lineEntryCount > recentLine.length && (
                <span className="text-[1.1rem] text-black">
                  LINE送信履歴は取得済み{recentLine.length}件を表示中（全
                  {lineCountLabel}）
                </span>
              )}
            </div>
            {visible.length === 0 ? (
              <p className="py-2 text-[1.3rem] text-black">
                LINE送信履歴以外の交渉記録はありません。
              </p>
            ) : (
              <div className="rounded-lg border border-gray-200">
                <table className="w-full table-fixed border-collapse [overflow-wrap:anywhere]">
                  <colgroup>
                    {/* 固定列をコンパクトにし、可変の「内容とメモ」に幅を残す。
                        日時は「YYYY/MM/DD」を 1 行に収める最小幅（本文 80px）で足りる。 */}
                    <col className="w-[16%] sm:w-[88px]" />
                    <col className="w-[12%] sm:w-[72px]" />
                    <col className="w-[12%] sm:w-[72px]" />
                    <col className="w-[10%] sm:w-[60px]" />
                    <col className="w-[12%] sm:w-[72px]" />
                    <col />
                    {!readOnly && <col className="w-[8%] sm:w-[44px]" />}
                  </colgroup>
                  <thead className="bg-gray-50">
                    <tr className="border-b border-gray-200 text-[1.1rem] text-black">
                      {[
                        "日時",
                        "記録者",
                        "手段",
                        "回線",
                        "連絡先",
                        "内容とメモ",
                        ...(readOnly ? [] : ["操作"]),
                      ].map((label) => (
                        <th key={label} scope="col" className="px-2 py-2 text-left font-semibold">
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-300">
                    {visible.map((entry) => (
                      <ContactLogRow
                        key={entry.id}
                        entry={entry}
                        disabled={isSubmitting}
                        readOnly={readOnly}
                        onEdit={() => openEdit(entry)}
                        onDelete={() => handleDelete(entry)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {(hiddenCount > 0 || expanded) && recent.length > COLLAPSED_COUNT && (
              <button
                type="button"
                onClick={() => setExpanded((value) => !value)}
                className="mt-2 text-[1.2rem] font-medium text-primary hover:opacity-70"
              >
                {expanded ? "折りたたむ" : `残り${hiddenCount}件を表示`}
              </button>
            )}
            {historyNextPage != null && (
              <button
                type="button"
                onClick={handleLoadMore}
                disabled={isSubmitting}
                className="ml-4 mt-2 text-[1.2rem] font-medium text-primary hover:opacity-70 disabled:opacity-50"
              >
                もっと見る{remainingHistoryCount > 0 ? `（未取得${remainingHistoryCount}件）` : ""}
              </button>
            )}
          </>
        )}
      </div>

      {lineMemoEdit && !readOnly && (
        // LINE の送信記録はメモだけ編集できる。保存時は他の項目も元の値のまま送り、
        // サーバはメモ以外を書き換えない（カウント・手段の変更は 422）
        <Modal title="メモを編集" onClose={reset}>
          <p className="text-[1.3rem] text-black">
            {CONTACT_CHANNEL_LABELS[form.channel] ?? form.channel}（
            {CONTACT_DIRECTION_LABELS[form.direction] ?? form.direction}）{" "}
            {dayjs(form.contactedAtLocal).format("YYYY/MM/DD HH:mm")}
          </p>
          <p className="mt-1 text-[1.1rem] text-black">
            LINE の送信記録はメモだけ編集できます（削除・連絡回数のカウント変更はできません）。
          </p>

          <label className="mt-4 block text-[1.3rem] font-medium text-black">
            メモ（任意）
            <textarea
              className="mt-2 w-full rounded-xl border border-gray-300 p-3 text-[1.3rem]"
              rows={3}
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            />
          </label>

          <label className="mt-3 flex items-center gap-2 text-[1.3rem] text-black">
            <input
              type="checkbox"
              checked={form.countsAsContact}
              readOnly
              disabled
              className={checkbox}
            />
            連絡回数にカウントする
          </label>

          {error && <p className="mt-2 text-[1.2rem] text-red-600">{error}</p>}

          <div className="mt-4 flex justify-end gap-2">
            <button type="button" className={ghostBtn} onClick={reset} disabled={isSubmitting}>
              キャンセル
            </button>
            <button type="button" className={primaryBtn} disabled={isSubmitting} onClick={submit}>
              保存
            </button>
          </div>
        </Modal>
      )}

      {mode && !readOnly && !lineMemoEdit && (
        <Modal title={mode === "edit" ? "連絡記録を編集" : "連絡を記録"} onClose={reset}>
          <fieldset>
            <legend className="text-[1.3rem] font-medium text-black">連絡手段</legend>
            {mode === "edit" && isSystemContactChannel(form.channel) ? (
              // システム自動記録（督促リフ・LINE一括等）のチャンネルは選択肢に
              // 存在しないため、ボタンで別チャンネルに上書きすると元に戻せない。
              // 表示のみにして書き換えを防ぐ
              <p className="mt-2 text-[1.3rem] text-black">
                {CONTACT_CHANNEL_LABELS[form.channel] ?? form.channel}
                <span className="ml-2 text-[1.1rem] text-black">
                  （システム記録のため変更できません）
                </span>
              </p>
            ) : (
              <SelectButtons
                options={CONTACT_CHANNEL_OPTIONS}
                selected={form.channel}
                onSelect={(channel) =>
                  setForm((f) =>
                    f.channel === channel
                      ? f
                      : applyDefaultCountsAsContact({
                          ...f,
                          channel,
                          // 回線は電話・SMS、内容は電話専用。対象外の手段へ切り替えたらクリアする
                          ...(hasPhoneLine(channel) ? {} : { phoneLine: "" as const }),
                          ...(channel === "phone" ? {} : { contactDetail: "" as const }),
                          // 前の手段で決めた計上の有無を持ち越さない
                          countsAsContactTouched: false,
                        })
                  )
                }
              />
            )}
          </fieldset>

          {hasPhoneLine(form.channel) && (
            <fieldset className="mt-4">
              <legend className="text-[1.3rem] font-medium text-black">チャンネル</legend>
              <SelectButtons
                options={CONTACT_PHONE_LINE_OPTIONS}
                selected={form.phoneLine}
                onSelect={(phoneLine) =>
                  setForm((f) => ({ ...f, phoneLine: f.phoneLine === phoneLine ? "" : phoneLine }))
                }
              />
            </fieldset>
          )}

          <div className="mt-4 flex flex-wrap gap-6">
            <fieldset>
              <legend className="text-[1.3rem] font-medium text-black">方向</legend>
              <div className="mt-2 flex gap-2">
                {(["outbound", "inbound"] as ContactDirection[]).map((dir) => (
                  <button
                    key={dir}
                    type="button"
                    aria-pressed={form.direction === dir}
                    onClick={() =>
                      setForm((f) => applyDefaultCountsAsContact({ ...f, direction: dir }))
                    }
                    className={`rounded-xl border px-4 py-2 text-[1.3rem] font-medium transition-colors ${
                      form.direction === dir
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-gray-300 text-black hover:bg-gray-50"
                    }`}
                  >
                    {CONTACT_DIRECTION_LABELS[dir]}
                  </button>
                ))}
              </div>
            </fieldset>

            <div>
              <label className="text-[1.3rem] font-medium text-black">
                連絡日時
                <input
                  type="datetime-local"
                  className="mt-2 block rounded-xl border border-gray-300 p-2 text-[1.3rem]"
                  value={form.contactedAtLocal}
                  max={nowLocal()}
                  aria-invalid={contactedAtError != null}
                  aria-describedby={contactedAtError ? "contacted-at-error" : undefined}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, contactedAtLocal: e.target.value }))
                    setContactedAtError(null)
                  }}
                />
              </label>
              {contactedAtError && (
                <p id="contacted-at-error" role="alert" className="mt-1 text-[1.2rem] text-red-600">
                  {contactedAtError}
                </p>
              )}
            </div>
          </div>

          <fieldset className="mt-4">
            <legend className="text-[1.3rem] font-medium text-black">
              {counterpartFieldLabel(form.channel)}
            </legend>
            <SelectButtons
              options={CONTACT_COUNTERPART_OPTIONS}
              selected={form.counterpart}
              onSelect={(counterpart) =>
                setForm((f) => ({
                  ...f,
                  counterpart: f.counterpart === counterpart ? "" : counterpart,
                }))
              }
            />
          </fieldset>

          <fieldset className="mt-4">
            <legend className="text-[1.3rem] font-medium text-black">接触</legend>
            <SelectButtons
              options={CONTACT_RESULT_OPTIONS}
              selected={form.contactResult}
              onSelect={(contactResult) =>
                setForm((f) => ({
                  ...f,
                  contactResult: f.contactResult === contactResult ? "" : contactResult,
                }))
              }
            />
            {/* 旧レコードの「結果」は表示のみ（編集して保存すると contactResult 側に移行してよい） */}
            {form.outcome && (
              <p className="mt-1 text-[1.1rem] text-black">
                旧記録の結果: {CONTACT_OUTCOME_LABELS[form.outcome] ?? form.outcome}
              </p>
            )}
          </fieldset>

          {form.channel === "phone" && (
            <fieldset className="mt-4">
              <legend className="text-[1.3rem] font-medium text-black">内容</legend>
              <SelectButtons
                options={CONTACT_DETAIL_OPTIONS}
                selected={form.contactDetail}
                onSelect={(contactDetail) =>
                  setForm((f) =>
                    applyDefaultCountsAsContact({
                      ...f,
                      contactDetail: f.contactDetail === contactDetail ? "" : contactDetail,
                    })
                  )
                }
              />
            </fieldset>
          )}

          <label className="mt-4 block text-[1.3rem] font-medium text-black">
            メモ（任意）
            <textarea
              className="mt-2 w-full rounded-xl border border-gray-300 p-3 text-[1.3rem]"
              rows={3}
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              placeholder="通話内容・経緯など"
            />
          </label>

          <label className="mt-3 flex items-center gap-2 text-[1.3rem] text-black">
            <input
              type="checkbox"
              checked={form.countsAsContact}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  countsAsContact: e.target.checked,
                  countsAsContactTouched: true,
                }))
              }
              className={checkbox}
            />
            連絡回数にカウントする
          </label>

          {error && <p className="mt-2 text-[1.2rem] text-red-600">{error}</p>}

          <div className="mt-4 flex justify-end gap-2">
            <button type="button" className={ghostBtn} onClick={reset} disabled={isSubmitting}>
              キャンセル
            </button>
            <button type="button" className={primaryBtn} disabled={isSubmitting} onClick={submit}>
              {mode === "edit" ? "保存" : "記録する"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  )
}
